import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '../../../../../request/hooks/useQuery';
import { ObjectsConfigAPI } from '../../../../api/ObjectsConfigAPI';
import { ObjectsFactory, ObjectsHeadersFactory } from '../../../factories/objects';
import {
  ObjectsHeadersRawData,
  ObjectsSelectionUpdatesPayload,
  ValidObjectsHeaders
} from '../../../interfaces/object-header';
import { ListMetaNetworkParams } from '../../../../../list-conductor/list-meta-data';
import { ObjectConfig, ObjectConfigStatus, ObjectsListRawData } from '../../../interfaces/object';
import { OffsetPaginationStrategy } from '../../../../../list-conductor/offset-pagination-strategy';
import { useMutation } from '../../../../../request/hooks/useMutation';
import { FilterList, ListNextPage, ListPrevPage } from '../../../../../list-conductor/list-actions';
import { Integration } from '../../../../../shared/interfaces/integration';
import { useObjectsSelection } from './useObjectsSelection';
import { DEFAULT_LIMIT } from '../../../../../list-conductor/constants';
import { isObjectEditable, objectHasDisabledStatus } from '../../../utils';
import { useAnalyticsTracker } from '../../../../../tracking/hooks/useAnalyticsTracker';
import { PipelineObjectConfigTrackers } from '../../../../tracking/pipeline-object-config-trackers';
import { DEFAULT_STRING_OPERATOR } from '../../../../../filter/constants';
import { SearchValueType } from '../../../../../input/SearchArea/interface';
import { ListConductorRawFilter } from '../../../../../list-conductor/filters';
import { OBJECT_CONFIG_SEARCH_FILTER_KEY } from '../../../constants/objects-config-filters';
import { getSearchValue } from '../../../../../shared/utils/filters';
import { useObjectsConfigStatus } from './useObjectsConfigStatus';
import { EntityUIState, MutationState } from '../../../../../request/interfaces/entity-ui-state';
import { getErrorMessageFromAxiosError } from '../../../../../request/utils';
import { getObjectsConfigColumns, ObjectsConfigTableGlobalContext } from '../ObjectsConfigColumns';
import { TableRow } from '../../../../../table/interfaces/table';

interface UseObjectsConfigProps {
  integration: Integration;
  onSave?: VoidFunction;
  isViewMode: boolean;
}

export function useObjectsConfig({ integration, onSave, isViewMode }: UseObjectsConfigProps) {
  const [fieldsSelectionModalConfig, setFieldsSelectionModalConfig] = useState<{
    visible: boolean;
    object?: ObjectConfig;
  }>({
    visible: false
  });
  const [objectsConfigHeaders, setObjectsConfigHeaders] = useState<ValidObjectsHeaders>();
  const { eventTrack } = useAnalyticsTracker();

  const {
    fetchObjectsConfigStatus,
    showStatusError,
    showStatusLoader,
    statusErrorMessage,
    showRetryCTAOnError,
    refreshCompleted,
    canFetchObjectsConfig,
    submittingRefreshSchemaRequest,
    isRefreshAndCatalogStatusComplete,
    refreshSchema
  } = useObjectsConfigStatus(integration.id, integration.sourceId!);

  const {
    isSelectionDeltaReset,
    selectionDelta,
    selected,
    total,
    isSelectionDeltaEmpty,
    isListIncluded,
    isListIndeterminate,
    resetSelectionDelta,
    setCountsIfReset,
    toggleItemInclusion,
    toggleListInclusion
  } = useObjectsSelection();

  const {
    data: objectsConfig,
    uiState: objectConfigUiState,
    error: objectsConfigFetchError,
    fetch: fetchObjectConfig,
    dispatchListAction,
    listConductor,
    writeData
  } = useQuery({
    dataSource$: async (listNetworkParams: ListMetaNetworkParams | undefined) => {
      if (!objectsConfigHeaders) {
        const _headers = await ObjectsConfigAPI.getObjectConfigHeader(
          integration.source!.sourceType.sourceType
        ).then(res => ObjectsHeadersFactory(res as ObjectsHeadersRawData));

        setObjectsConfigHeaders(_headers);
      }

      const networkParamsToSend = getAPICompatibleObjectListNetworkParams(listNetworkParams);

      const getObjectsRequest$ = isViewMode
        ? ObjectsConfigAPI.getObjectsConfigForView.bind(ObjectsConfigAPI)
        : ObjectsConfigAPI.getObjectsConfig.bind(ObjectsConfigAPI);

      const res = await getObjectsRequest$(networkParamsToSend, integration.id);
      const rawResponse = res.data as ObjectsListRawData;

      const items = ObjectsFactory(rawResponse.data);

      if (isSelectionDeltaReset()) {
        setCountsIfReset({
          total: rawResponse.total_count,
          selected: rawResponse.selected_count,
          disabledCount: rawResponse.disabled_count || 0
        });
      } else {
        items.forEach(item => {
          const newItem = item;

          if ((selectionDelta.inclusions || []).includes(item.id)) {
            newItem.status = ObjectConfigStatus.ACTIVE;
            return;
          }

          if ((selectionDelta.exclusions || []).includes(item.id)) {
            newItem.status = ObjectConfigStatus.INACTIVE;
            return;
          }

          if (
            selectionDelta.allSelect === true &&
            !objectHasDisabledStatus(item.status) &&
            isObjectEditable(item)
          ) {
            newItem.status = ObjectConfigStatus.ACTIVE;
            return;
          }

          if (selectionDelta.allSelect === false) {
            newItem.status = ObjectConfigStatus.INACTIVE;
          }
        });
      }

      return {
        responseMetaData: {
          count: rawResponse.total_count,
          page: rawResponse.page
        },
        items
      };
    },
    paginated: true,
    listConductorOptions: {
      handleQueryParams: false,
      filterKeys: [OBJECT_CONFIG_SEARCH_FILTER_KEY],
      paginationStrategy: new OffsetPaginationStrategy(DEFAULT_LIMIT)
    }
  });

  const { mutationState, mutate: saveChanges } = useMutation({
    mutationTarget$: () =>
      ObjectsConfigAPI.saveObjectSelection(getObjectsUpdatesPayload(), integration.id),
    onMutationSuccess: () => {
      if (typeof onSave === 'function') {
        onSave();
      }
    }
  });

  const pollObjectsConfigStatus = () => {
    fetchObjectsConfigStatus();
    resetSelectionDelta();
  };

  const fetchObjectsConfig = (resetSearch?: boolean) => {
    if (resetSearch) {
      onSearch('', false);
    } else {
      resetSelectionDelta();
      fetchObjectConfig();
    }
  };

  useEffect(() => {
    pollObjectsConfigStatus();
  }, []);

  useEffect(() => {
    if (canFetchObjectsConfig) {
      fetchObjectsConfig(refreshCompleted);
    }
  }, [refreshCompleted, canFetchObjectsConfig]);

  const canSaveSelectionInBackground = () => {
    return !isSelectionDeltaEmpty;
  };

  const saveSelectionInBackground = async () => {
    if (canSaveSelectionInBackground()) {
      try {
        await ObjectsConfigAPI.saveObjectSelection(getObjectsUpdatesPayload(), integration.id);
      } catch (e) {
        // do not handle
      }
    }
  };

  const onPrev = () => {
    dispatchListAction(new ListPrevPage());
  };

  const onNext = () => {
    dispatchListAction(new ListNextPage());
  };

  const onSearch = useCallback(
    (searchTerm: SearchValueType, saveSelection = true) => {
      const _searchTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm.value;

      eventTrack({
        action: PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_CONFIG_SEARCH,
        properties: {
          pipelineId: integration.id,
          search: searchTerm
        }
      });

      if (!isViewMode && saveSelection) {
        (async () => {
          await saveSelectionInBackground();
        })();
      }

      resetSelectionDelta();

      dispatchListAction(
        new FilterList([
          {
            filter: OBJECT_CONFIG_SEARCH_FILTER_KEY,
            operator:
              typeof searchTerm !== 'string' ? searchTerm?.operator : DEFAULT_STRING_OPERATOR.value,
            value: _searchTerm
          }
        ])
      );
    },
    [isSelectionDeltaEmpty, selectionDelta, isViewMode]
  );

  const updateObject = (objectID: string, changes: Partial<ObjectConfig>) => {
    if (!objectsConfig) {
      return;
    }

    const objectIndex = objectsConfig.findIndex(object => object.id === objectID);

    if (objectIndex < 0) {
      return;
    }

    const updatedObject = {
      ...objectsConfig[objectIndex],
      ...changes
    } as ObjectConfig;

    writeData(() => {
      return [
        ...objectsConfig.slice(0, objectIndex),
        updatedObject,
        ...objectsConfig.slice(objectIndex + 1)
      ];
    });
  };

  const changeCheckedState = (objectID: string, included: boolean) => {
    updateObject(objectID, {
      status: included ? ObjectConfigStatus.ACTIVE : ObjectConfigStatus.INACTIVE
    });

    toggleItemInclusion(objectID, included);
  };

  const onObjectFieldsUpdate = (object: ObjectConfig) => {
    updateObject(object.id, object);

    const objectRef = (objectsConfig || []).find(o => object.id === o.id);

    if (objectRef?.status === object.status) {
      return;
    }

    toggleItemInclusion(object.id, true);
  };

  const onObjectAction = (object: ObjectConfig) => {
    updateObject(object.id, object);
  };

  const changeSelectAllCheckedState = (included: boolean) => {
    writeData(() => {
      if (!objectsConfig) {
        return [];
      }

      return objectsConfig.map(object => {
        let newStatus = object.status;

        if (!objectHasDisabledStatus(object.status) && isObjectEditable(object)) {
          newStatus = included ? ObjectConfigStatus.ACTIVE : ObjectConfigStatus.INACTIVE;
        }

        return {
          ...object,
          status: newStatus
        };
      });
    });

    toggleListInclusion(included);
  };

  const getObjectsUpdatesPayload = (): ObjectsSelectionUpdatesPayload => {
    const filters = listConductor?.getListMetaNetworkParams().filters as ListConductorRawFilter[];
    const searchTerm = getSearchValue(filters, OBJECT_CONFIG_SEARCH_FILTER_KEY) || undefined;

    return {
      // all_select: !!selectionDelta.allSelect, // TODO
      all_select: selectionDelta.allSelect,
      included_list: selectionDelta.inclusions,
      excluded_list: selectionDelta.exclusions,
      search_key: searchTerm
    };
  };

  const getAPICompatibleObjectListNetworkParams = (
    listNetworkParams: ListMetaNetworkParams | undefined
  ) => {
    let networkParamsToSend = listNetworkParams;
    const listConductorRawFilter = listNetworkParams?.filters as ListConductorRawFilter[];

    if (listConductorRawFilter?.length) {
      const filters = [
        {
          operator: 'SEARCH_CASE_INSENSITIVE',
          values: listConductorRawFilter[0].values
        }
      ];

      networkParamsToSend = { ...networkParamsToSend, filters };
    }

    return networkParamsToSend;
  };

  const submitForm = () => {
    eventTrack({
      action: PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_CONFIG_CONTINUE_CLICK,
      properties: {
        pipelineId: integration.id
      }
    });

    saveChanges();
  };

  const _setFieldsSelectionModalConfig = (
    params: { visible: boolean; object?: ObjectConfig },
    saveSelection?: boolean
  ) => {
    if (!isViewMode && saveSelection) {
      (async () => {
        await saveSelectionInBackground();
      })();
    }

    setFieldsSelectionModalConfig(params);
  };

  const setOpenFieldsSelectionModalConfig = (open: boolean) => {
    eventTrack({
      action:
        PipelineObjectConfigTrackers.PIPELINE_TRACKER_PIPELINE_OBJECT_FIELDS_CONFIG_SELECTION_MODE_CONFIG_MODAL_TOGGLE,
      properties: {
        open
      }
    });

    setFieldsSelectionModalConfig({ visible: open });
  };

  const columns = useMemo(() => {
    if (!objectsConfigHeaders) {
      return [];
    }

    return getObjectsConfigColumns(objectsConfigHeaders, isViewMode);
  }, [objectsConfigHeaders]);

  const rows: TableRow<ObjectConfig>[] = useMemo(() => {
    return (objectsConfig || []).map(objectConfig => ({
      data: objectConfig,
      tabIndex: 0,
      id: objectConfig.id
    }));
  }, [objectsConfig]);

  const showObjectsConfigWidget =
    isRefreshAndCatalogStatusComplete && objectConfigUiState !== EntityUIState.INIT;
  const showConfigTable = objectConfigUiState === EntityUIState.IDLE;

  const showConfigRetryAlert =
    objectConfigUiState === EntityUIState.ERRORED && !!objectsConfigFetchError;

  const showEmptyTableWidget =
    objectConfigUiState === EntityUIState.EMPTY || objectConfigUiState === EntityUIState.NEW;

  const showPaginationControls =
    objectConfigUiState === EntityUIState.IDLE ||
    objectConfigUiState === EntityUIState.LOADING_MORE;

  const showTableLoadingShimmer =
    objectConfigUiState === EntityUIState.LOADING ||
    objectConfigUiState === EntityUIState.LOADING_MORE;

  const objectsConfigFetchErrorMessage = objectsConfigFetchError
    ? getErrorMessageFromAxiosError(objectsConfigFetchError)
    : undefined;

  const savingForm = mutationState === MutationState.MUTATING;
  const disableSubmitCTA = savingForm || showStatusLoader || showTableLoadingShimmer;

  const objectsTableGlobalContext: ObjectsConfigTableGlobalContext = {
    pipelineId: integration.id,
    selected,
    total,
    isViewMode,
    isListIncluded,
    isListIndeterminate,
    changeCheckedState,
    changeSelectAllCheckedState,
    setFieldsSelectionModalConfig: _setFieldsSelectionModalConfig,
    onObjectAction
  };

  return {
    isSelectionDeltaEmpty,
    objectConfigUiState,
    objectsConfigFetchError,
    listConductor,
    fetchObjectsConfigStatus,
    fetchObjectConfig,
    onObjectFieldsUpdate,
    onSearch,
    onNext,
    onPrev,
    pollObjectsConfigStatus,
    refreshSchema,
    submitForm,
    rows,
    columns,
    showStatusError,
    showStatusLoader,
    statusErrorMessage,
    showRetryCTAOnError,
    showObjectsConfigWidget,
    showConfigTable,
    submittingRefreshSchemaRequest,
    showConfigRetryAlert,
    showEmptyTableWidget,
    showPaginationControls,
    showTableLoadingShimmer,
    objectsConfigFetchErrorMessage,
    disableSubmitCTA,
    savingForm,
    objectsTableGlobalContext,
    fieldsSelectionModalConfig,
    setOpenFieldsSelectionModalConfig
  };
}
